import { useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";

const FADE_OUT_DURATION = 700;

type Direction = "right" | "left";

interface FadingOutEntry {
  id: string;
  direction: Direction;
}

interface UseAnimatedMessageResolveOptions {
  animationDuration?: number;
}

interface UseAnimatedMessageResolveReturn {
  fadingOutEntry: FadingOutEntry | null;
  executeWithAnimation: (
    messageId: string,
    direction: Direction,
    apiCall: () => Promise<unknown>,
    onComplete?: () => void | Promise<void>,
  ) => void;
}

/**
 * メッセージ／コメントの解決・未解決切り替え時に CSS アニメーションと
 * API 呼び出しを協調させる汎用フック。
 *
 * - direction="right": 進行中→完了（fade-out-right）
 * - direction="left":  完了→進行中（fade-out-left）
 *
 * apiCall と animationDuration を並列で待機し、両方が完了してから
 * onComplete を実行する。ローカルステート更新は onComplete 内で行うこと。
 * これにより、アニメーション中にフィルタリングが再計算されるのを防ぐ。
 */
export const useAnimatedMessageResolve = ({
  animationDuration = FADE_OUT_DURATION,
}: UseAnimatedMessageResolveOptions = {}): UseAnimatedMessageResolveReturn => {
  const [fadingOutEntry, setFadingOutEntry] = useState<FadingOutEntry | null>(null);
  const isRunning = useRef(false);

  const executeWithAnimation = useCallback(
    (
      messageId: string,
      direction: Direction,
      apiCall: () => Promise<unknown>,
      onComplete?: () => void | Promise<void>,
    ) => {
      if (isRunning.current) return;
      isRunning.current = true;

      const run = async () => {
        try {
          setFadingOutEntry({ id: messageId, direction });

          await Promise.all([
            apiCall(),
            new Promise<void>((r) => setTimeout(r, animationDuration)),
          ]);

          isRunning.current = false;
          setFadingOutEntry(null);
          await onComplete?.();
        } catch {
          isRunning.current = false;
          setFadingOutEntry(null);
          toast.error("メッセージの更新に失敗しました");
        }
      };

      run();
    },
    [animationDuration],
  );

  return { fadingOutEntry, executeWithAnimation };
};
